import { Select, SelectProps } from "@mantine/core"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react"
import { DefaultActionIcon } from "../../-components/action-icons"
import { SelectStartsWithFilter } from "../../-components/select/select-word-start-filter"

const weeks = Array.from({ length: 52 }, (_, i) => i + 1)

type Props = {
  value: Date | null
  onChange: (value: Date | null) => void
  minDate?: Date
  maxDate?: Date
  weeks?: number[]
  withoutYearPicker?: boolean
  year: Date
} & Omit<
  SelectProps,
  "data" | "value" | "onClick" | "onFocus" | "onBlur" | "styles" | "onChange"
>

const dateFormat = "DD.MM.YYYY"

export function WeekPicker({
  value,
  onChange,
  minDate,
  maxDate,
  weeks: weeksProp,
  year,
  ...textInputProps
}: Props) {
  const { t } = useTranslation("app_server")

  const firstDayOfYear = React.useMemo(
    () => dayjs(year).startOf("year"),
    [year]
  )

  const [weeksMap, weekStartDates] = React.useMemo(() => {
    const startOfWeek = firstDayOfYear.add(3, "days").startOf("week")
    let startDates = weeks.map((week) =>
      startOfWeek.add((week - 1) * 7, "days")
    )

    startDates = startDates.filter((start) => {
      const end = start.add(6, "days")
      return (
        (!minDate || end.isSameOrAfter(minDate)) &&
        (!maxDate || start.isSameOrBefore(maxDate))
      )
    })

    const map: Record<string, dayjs.Dayjs> = {}
    startDates.forEach((start) => {
      map[start.toISOString()] = start
    })

    return [map, startDates]
  }, [firstDayOfYear, minDate, maxDate])

  const data = React.useMemo(() => {
    return weekStartDates
      .filter((start) => !weeksProp || weeksProp.includes(start.week()))
      .map((start) => {
        const label = `${start.week()}. ${t("CW_CalendarWeek")} - ${
          minDate && dayjs(minDate).isAfter(start)
            ? dayjs(minDate).format(dateFormat)
            : start.format(dateFormat)
        }`

        return { value: start.toISOString(), label }
      })
  }, [weekStartDates, weeksProp, t, minDate])

  const selectedWeek = React.useMemo(() => {
    if (value) return weeksMap[value.toISOString()]

    if (data.length > 0) {
      const today = dayjs()
      const lastWeekStart = dayjs(data[data.length - 1].value)
      const firstWeekStart = dayjs(data[0].value)

      const fallback = today.isAfter(lastWeekStart)
        ? data[data.length - 1]
        : today.isBefore(firstWeekStart)
          ? data[0]
          : null

      if (fallback) {
        onChange(new Date(fallback.value))
        return weeksMap[fallback.value]
      }
    }

    return null
  }, [value, data, weeksMap, onChange])

  const currentIndex = React.useMemo(() => {
    if (!selectedWeek) return -1
    return data.findIndex((d) => d.value === selectedWeek.toISOString())
  }, [data, selectedWeek])

  const goToWeek = (indexDelta: number) => {
    const nextIndex = currentIndex + indexDelta
    if (nextIndex >= 0 && nextIndex < data.length) {
      const next = data[nextIndex]
      onChange(next?.value ? new Date(next.value) : null)
    }
  }

  return (
    <Select
      checkIconPosition="right"
      searchable
      label={t("CW_CalendarWeek")}
      data={data}
      value={selectedWeek?.toISOString() ?? null}
      onChange={(val) => onChange(val ? new Date(val) : null)}
      filter={SelectStartsWithFilter}
      leftSection={
        <DefaultActionIcon
          size="sm"
          variant="subtle"
          onClick={() => goToWeek(-1)}
          disabled={currentIndex <= 0}
        >
          <IconArrowLeft size={16} />
        </DefaultActionIcon>
      }
      rightSectionPointerEvents="auto"
      rightSection={
        <DefaultActionIcon
          size="sm"
          variant="subtle"
          onClick={() => goToWeek(1)}
          disabled={currentIndex === -1 || currentIndex >= data.length - 1}
        >
          <IconArrowRight size={16} />
        </DefaultActionIcon>
      }
      {...textInputProps}
    />
  )
}
